import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RadarIcon from "@mui/icons-material/Radar";
import GoogleIcon from "@mui/icons-material/Google";
import { useAuth } from "../contexts/AuthContext";
import UpgradeAccountDialog from "../components/UpgradeAccountDialog";
import {
  getLocaleSettings,
  updateLocale,
  getCalendarStatus,
  getKeywords,
  updateKeywords,
  sendBotToMeeting,
  scanUpcomingMeetings,
  confirmMeetingSelection,
  getUserProfile,
  calendarAuth,
} from "../utils/firebase";

export default function SettingsPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [settings, setSettings] = useState(null);

  // Connection & User profile
  const [connected, setConnected] = useState(null);
  const [calendarEmail, setCalendarEmail] = useState(null);
  const [calendarHealthy, setCalendarHealthy] = useState(true);
  const [canManageMeetings, setCanManageMeetings] = useState(false);

  // Scan Suggested Calls
  const [scanMeetings, setScanMeetings] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanLoaded, setScanLoaded] = useState(false);
  const [selections, setSelections] = useState({});
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);
  const [newSuggestedKeyword, setNewSuggestedKeyword] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmBanner, setConfirmBanner] = useState(null);

  // Keywords
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [manualMeetingUrl, setManualMeetingUrl] = useState("");
  const [manualSendLoading, setManualSendLoading] = useState(false);
  const [manualSendResult, setManualSendResult] = useState(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const checkConnection = useCallback(async () => {
    try {
      const result = await getCalendarStatus();
      setConnected(result.data.connected);
      setCalendarEmail(result.data.email || null);
      setCalendarHealthy(result.data.healthy !== false);
    } catch {
      setConnected(false);
      setCalendarEmail(null);
      setCalendarHealthy(true);
    }
  }, []);

  const loadUserProfile = useCallback(async () => {
    try {
      const result = await getUserProfile();
      const { teamId, teamRole } = result.data || {};
      setCanManageMeetings(!teamId || teamRole === "admin");
    } catch (err) {
      console.error("Failed to load user profile:", err);
      setCanManageMeetings(false);
    }
  }, []);

  const loadKeywords = useCallback(async () => {
    try {
      const result = await getKeywords();
      setKeywords(result.data.keywords || []);
    } catch (err) {
      console.error("Failed to load keywords:", err);
    }
  }, []);

  const loadScan = useCallback(async () => {
    setScanLoading(true);
    try {
      const result = await scanUpcomingMeetings();
      const meetings = result.data?.meetings || [];
      setScanMeetings(meetings);
      const initial = {};
      meetings.forEach((m) => {
        initial[m.eventId] = m.confidence === "high";
      });
      setSelections(initial);
      setSuggestedKeywords(result.data?.keywords || []);
    } catch (err) {
      console.error("Failed to scan meetings:", err);
    } finally {
      setScanLoading(false);
      setScanLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setError(null);
    getLocaleSettings()
      .then((r) => setSettings(r.data || null))
      .catch((err) => setError(err.message || t("settings.failedLoad")))
      .finally(() => setLoading(false));

    checkConnection();
    loadUserProfile();
    loadKeywords();
  }, [user, t, checkConnection, loadUserProfile, loadKeywords]);

  useEffect(() => {
    if (connected === true && canManageMeetings && !scanLoaded && !scanLoading) {
      loadScan();
    }
  }, [connected, canManageMeetings, scanLoaded, scanLoading, loadScan]);

  const handleConnect = async () => {
    try {
      const result = await calendarAuth();
      const popup = window.open(result.data.url, "_blank", "width=600,height=700");
      const poll = setInterval(async () => {
        if (!popup || popup.closed) {
          clearInterval(poll);
          await checkConnection();
        }
      }, 1000);
    } catch (err) {
      setError(t("dashboard.failedCalendarConnection"));
    }
  };

  const handleChangeLanguage = async (event) => {
    const newLocale = event.target.value;
    if (!newLocale || newLocale === settings?.effective) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await updateLocale({ locale: newLocale });
      const r = await getLocaleSettings();
      setSettings(r.data || null);
      setInfo(t("settings.saved"));
    } catch (err) {
      setError(err.message || t("settings.failedSave"));
    } finally {
      setSaving(false);
    }
  };

  const saveKeywords = async (updated) => {
    setKeywords(updated);
    try {
      await updateKeywords({ keywords: updated });
    } catch (err) {
      console.error("Failed to save keywords:", err);
    }
  };

  const handleAddKeyword = () => {
    const trimmed = newKeyword.trim();
    if (!trimmed) return;
    if (keywords.some((k) => k.toLowerCase() === trimmed.toLowerCase())) {
      setNewKeyword("");
      return;
    }
    saveKeywords([...keywords, trimmed]);
    setNewKeyword("");
  };

  const handleDeleteKeyword = (keyword) => {
    saveKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleManualSendBot = async () => {
    const url = manualMeetingUrl.trim();
    if (!url) return;
    setManualSendLoading(true);
    setManualSendResult(null);
    try {
      const result = await sendBotToMeeting({ meetingUrl: url });
      setManualSendResult(result.data?.success ? t("dashboard.botSentSuccess") : result.data?.error || "Failed");
    } catch (err) {
      if (err.code === "functions/resource-exhausted") {
        setUpgradeDialogOpen(true);
      } else {
        setManualSendResult("Error: " + err.message);
      }
    } finally {
      setManualSendLoading(false);
    }
  };

  const handleToggleSelection = (eventId) => {
    setSelections((prev) => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const handleAddSuggestedKeyword = () => {
    const k = newSuggestedKeyword.trim();
    if (!k) return;
    if (suggestedKeywords.some((x) => x.toLowerCase() === k.toLowerCase())) {
      setNewSuggestedKeyword("");
      return;
    }
    setSuggestedKeywords((prev) => [...prev, k]);
    setNewSuggestedKeyword("");
  };

  const handleRemoveSuggestedKeyword = (k) => {
    setSuggestedKeywords((prev) => prev.filter((x) => x !== k));
  };

  const handleConfirmSelection = async () => {
    setConfirming(true);
    setConfirmBanner(null);
    setError(null);
    try {
      const prefs = {};
      scanMeetings.forEach((m) => {
        prefs[m.eventId] = selections[m.eventId] ? "join" : "skip";
      });
      const result = await confirmMeetingSelection({ prefs, keywords: suggestedKeywords });
      const scheduled = result.data?.scheduled ?? 0;
      const limitReachedCount = (result.data?.results || []).filter((r) => r.status === "limit_reached").length;
      if (limitReachedCount > 0) {
        setUpgradeDialogOpen(true);
        setConfirmBanner(
          scheduled === 0
            ? t("dashboard.limitReachedAll", { count: limitReachedCount })
            : t("dashboard.scheduledWithLimit", { scheduled, limit: limitReachedCount })
        );
      } else {
        setConfirmBanner(t("scan.confirmedBanner", { count: scheduled }));
      }
      setScanLoaded(false); // trigger fresh scan
      await loadKeywords(); // refresh keywords settings
    } catch (err) {
      console.error("Confirm failed:", err);
      setError(t("scan.confirmFailed"));
    } finally {
      setConfirming(false);
    }
  };

  if (authLoading || !user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  const managedByAdmin = !!settings?.managedByAdmin;
  const currentValue = settings?.effective || "en";

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Cal Sans", "Inter", sans-serif',
          fontWeight: 600,
          color: "#111827",
          mb: 1
        }}
      >
        {t("settings.title")}
      </Typography>
      <Typography variant="body2" sx={{ color: "#6b7280", mb: 4 }}>
        {t("settings.subtitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}
      {confirmBanner && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setConfirmBanner(null)}>
          {confirmBanner}
        </Alert>
      )}

      <Box display="flex" flexDirection="column" gap={4}>
        {/* Step 1: Calendar Connection */}
        <Card sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5 }}>
          <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "#111827",
                color: "#ffffff",
                fontFamily: '"Cal Sans", sans-serif',
                fontSize: 14,
                fontWeight: 600
              }}>
                1
              </Box>
              <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>
                {t("settings.step1Title")}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b7280", mb: 3 }}>
              {t("settings.step1Desc")}
            </Typography>

            {connected === true ? (
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    bgcolor: calendarHealthy ? "#10b981" : "#f59e0b",
                    borderRadius: "50%"
                  }} />
                  <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 500 }}>
                    {calendarHealthy 
                      ? calendarEmail 
                        ? t("settings.step1Connected", { email: calendarEmail }) 
                        : t("settings.step1ConnectedActive")
                      : t("settings.step1ConnectionExpired", { email: calendarEmail })}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={handleConnect}
                  sx={{ textTransform: "none" }}
                >
                  {calendarHealthy ? t("settings.step1ChangeAccount") : t("settings.step1Reconnect")}
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                startIcon={<GoogleIcon />}
                onClick={handleConnect}
                sx={{
                  bgcolor: "#111827",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#374151" }
                }}
              >
                {t("dashboard.connectGoogleCalendar")}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Language Card */}
        <Card sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5 }}>
          <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "#111827",
                color: "#ffffff",
                fontFamily: '"Cal Sans", sans-serif',
                fontSize: 14,
                fontWeight: 600
              }}>
                2
              </Box>
              <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>
                {t("settings.reportLanguage.title")}
              </Typography>
              {managedByAdmin && (
                <Chip label={t("settings.reportLanguage.managedByAdminBadge")} size="small" />
              )}
            </Box>

            <Typography variant="body2" sx={{ color: "#6b7280", mb: 3 }}>
              {managedByAdmin
                ? t("settings.reportLanguage.helperMember")
                : t("settings.reportLanguage.helperAdmin")}
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" py={1}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <FormControl fullWidth disabled={managedByAdmin || saving} size="small" sx={{ maxWidth: 300 }}>
                <InputLabel id="report-language-label">
                  {t("settings.reportLanguage.label")}
                </InputLabel>
                <Select
                  labelId="report-language-label"
                  label={t("settings.reportLanguage.label")}
                  value={currentValue}
                  onChange={handleChangeLanguage}
                >
                  <MenuItem value="en">{t("settings.reportLanguage.optionEn")}</MenuItem>
                  <MenuItem value="es">{t("settings.reportLanguage.optionEs")}</MenuItem>
                  <MenuItem value="fr">{t("settings.reportLanguage.optionFr")}</MenuItem>
                  <MenuItem value="de">{t("settings.reportLanguage.optionDe")}</MenuItem>
                  <MenuItem value="it">{t("settings.reportLanguage.optionIt")}</MenuItem>
                  <MenuItem value="pt">{t("settings.reportLanguage.optionPt")}</MenuItem>
                  <MenuItem value="nl">{t("settings.reportLanguage.optionNl")}</MenuItem>
                  <MenuItem value="pl">{t("settings.reportLanguage.optionPl")}</MenuItem>
                  <MenuItem value="ja">{t("settings.reportLanguage.optionJa")}</MenuItem>
                  <MenuItem value="ko">{t("settings.reportLanguage.optionKo")}</MenuItem>
                  <MenuItem value="zh">{t("settings.reportLanguage.optionZh")}</MenuItem>
                </Select>
              </FormControl>
            )}
          </CardContent>
        </Card>

        {/* Step 3: Meeting Tracking (Smart finder + Keywords Rules) */}
        <Card sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5 }}>
          <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "#111827",
                color: "#ffffff",
                fontFamily: '"Cal Sans", sans-serif',
                fontSize: 14,
                fontWeight: 600
              }}>
                3
              </Box>
              <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>
                {t("settings.step3Title")}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b7280", mb: 4 }}>
              {t("settings.step3Desc")}
            </Typography>

            {connected !== true ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t("settings.step3ConnectAlert")}
              </Alert>
            ) : !canManageMeetings ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t("dashboard.teamAdminKeywords")}
              </Alert>
            ) : (
              <Box display="flex" flexDirection="column" gap={4}>
                {/* Keywords Rules */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontSize: 14, fontWeight: 600, color: "#111827", mb: 1 }}>
                    {t("dashboard.keywordsTitle")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", mb: 2.5 }}>
                    {t("dashboard.keywordsDesc")}
                  </Typography>

                  <Box display="flex" flexWrap="wrap" gap={1} mb={2.5}>
                    {keywords.map((kw) => (
                      <Chip
                        key={kw}
                        label={kw}
                        onDelete={() => handleDeleteKeyword(kw)}
                        sx={{
                          bgcolor: "#111827",
                          color: "#ffffff",
                          fontWeight: 500,
                          fontSize: 13,
                          "& .MuiChip-deleteIcon": { color: "#9ca3af", "&:hover": { color: "#ffffff" } },
                        }}
                      />
                    ))}
                    {keywords.length === 0 && (
                      <Typography sx={{ color: "#9ca3af", fontSize: 13 }}>
                        {t("settings.step3NoKeywords")}
                      </Typography>
                    )}
                  </Box>

                  <Box display="flex" gap={1} alignItems="center" mb={2}>
                    <TextField
                      size="small"
                      placeholder={t("dashboard.keywordPlaceholder")}
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      sx={{
                        flex: 1,
                        maxWidth: 300,
                        "& .MuiOutlinedInput-root": { bgcolor: "#ffffff" },
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddKeyword}
                      disabled={!newKeyword.trim()}
                      sx={{ textTransform: "none" }}
                    >
                      {t("dashboard.add")}
                    </Button>
                  </Box>
                </Box>

                {/* Suggested Calls (scan) section */}
                {(scanLoading || scanMeetings.length > 0) && (
                  <Box sx={{ pt: 4, borderTop: "1px solid #e5e7eb" }}>
                    <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                      <RadarIcon sx={{ fontSize: 20, color: "#111827" }} />
                      <Typography variant="subtitle2" sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                        {t("scan.heading")}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#6b7280", mb: 3 }}>
                      {t("scan.subtitle")}
                    </Typography>

                    {scanLoading ? (
                      <Box display="flex" alignItems="center" gap={1.5} py={2}>
                        <CircularProgress size={20} />
                        <Typography sx={{ color: "#6b7280", fontSize: 13 }}>{t("scan.scanning")}</Typography>
                      </Box>
                    ) : (
                      <>
                        <Box display="flex" flexDirection="column" gap={1.5} mb={2.5}>
                          {scanMeetings.map((m) => (
                            <Box
                              key={m.eventId}
                              sx={{
                                p: 1.5,
                                borderRadius: 1.5,
                                border: "1px solid #e5e7eb",
                                bgcolor: "#ffffff",
                              }}
                            >
                              <FormControlLabel
                                sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                                control={
                                  <Checkbox
                                    checked={Boolean(selections[m.eventId])}
                                    onChange={() => handleToggleSelection(m.eventId)}
                                    sx={{ pt: 0, color: "#111827", "&.Mui-checked": { color: "#111827" } }}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>
                                      {m.title}
                                    </Typography>
                                    <Typography sx={{ color: "#6b7280", fontSize: 12 }}>
                                      {new Date(m.timestamp).toLocaleDateString(undefined, {
                                        weekday: "short",
                                        day: "numeric",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </Typography>
                                    {m.reason && (
                                      <Typography sx={{ color: "#111827", fontSize: 12, mt: 0.5 }}>
                                        {m.reason}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </Box>
                          ))}
                        </Box>

                        <Typography sx={{ color: "#9ca3af", fontSize: 11, mb: 3 }}>
                          {t("scan.privacyNote")}
                        </Typography>

                        {/* Suggested Keywords rules inside suggested meetings scan */}
                        <Box
                          sx={{
                            p: 2.5,
                            mb: 3,
                            borderRadius: 1.5,
                            border: "1px dashed #cbd5e1",
                            bgcolor: "#f9fafb",
                          }}
                        >
                          <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 13, mb: 0.5 }}>
                            {t("scan.keywordsTitle")}
                          </Typography>
                          <Typography sx={{ color: "#6b7280", fontSize: 12, mb: 2 }}>
                            {t("scan.keywordsHelp")}
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                            {suggestedKeywords.map((kw) => (
                              <Chip
                                key={kw}
                                label={kw}
                                onDelete={() => handleRemoveSuggestedKeyword(kw)}
                                sx={{ bgcolor: "#ffffff", border: "1px solid #cbd5e1", color: "#111827", fontWeight: 500, fontSize: 13 }}
                              />
                            ))}
                            {suggestedKeywords.length === 0 && (
                              <Typography sx={{ color: "#9ca3af", fontSize: 12 }}>
                                {t("scan.keywordsEmpty")}
                              </Typography>
                            )}
                          </Box>
                          <Box display="flex" gap={1} alignItems="center">
                            <TextField
                              size="small"
                              placeholder={t("dashboard.keywordPlaceholder")}
                              value={newSuggestedKeyword}
                              onChange={(e) => setNewSuggestedKeyword(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddSuggestedKeyword();
                                }
                              }}
                              sx={{ flex: 1, maxWidth: 280, "& .MuiOutlinedInput-root": { bgcolor: "#ffffff" } }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={handleAddSuggestedKeyword}
                              disabled={!newSuggestedKeyword.trim()}
                              sx={{ textTransform: "none" }}
                            >
                              {t("dashboard.add")}
                            </Button>
                          </Box>
                        </Box>

                        <Button
                          variant="contained"
                          onClick={handleConfirmSelection}
                          disabled={confirming}
                          startIcon={confirming ? <CircularProgress size={18} color="inherit" /> : <RadarIcon />}
                          sx={{ bgcolor: "#111827", textTransform: "none", "&:hover": { bgcolor: "#374151" } }}
                        >
                          {confirming
                            ? t("scan.confirming")
                            : t("scan.confirmButton", { count: Object.values(selections).filter(Boolean).length })}
                        </Button>
                      </>
                    )}
                  </Box>
                )}

                {/* Send Bot Manually Section */}
                <Box sx={{ pt: 4, borderTop: "1px solid #e5e7eb" }}>
                  <Typography variant="body2" sx={{ color: "#6b7280", fontSize: 13, mb: 1.5 }}>
                    {t("dashboard.botNotJoining")}
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <TextField
                      size="small"
                      placeholder="https://meet.google.com/abc-defg-hij"
                      value={manualMeetingUrl}
                      onChange={(e) => setManualMeetingUrl(e.target.value)}
                      sx={{
                        flex: 1,
                        maxWidth: 400,
                        "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", fontSize: 13 },
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleManualSendBot}
                      disabled={!manualMeetingUrl.trim() || manualSendLoading}
                      sx={{ textTransform: "none" }}
                    >
                      {manualSendLoading ? t("dashboard.sending") : t("dashboard.sendBotNow")}
                    </Button>
                  </Box>
                  {manualSendResult && (
                    <Typography sx={{ color: manualSendResult.includes("success") ? "#059669" : "#dc2626", fontSize: 12, mt: 1 }}>
                      {manualSendResult}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <UpgradeAccountDialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} />
    </Container>
  );
}
